import React from 'react';
import { Box } from '@myob/myob-widgets';
import { TermsConditions } from 'components/terms/Terms';
import { ProductData } from 'hooks/useGetProduct/types';
import { useProductData } from 'hooks/useProductData/useProductData';
import { getTagPrice } from 'utils/pricingUtils';
import isNZProduct from 'utils/IsNZProduct';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { FEELIX_THEMES } from '../../../constants';
import { PricingCard } from '../pricingCard/PricingCard';
import defaultStyle from './ProductFooter.module.scss';
import commonStyle from './ProductFooter.common.module.scss';
import { FeatureList } from '../featureList/FeatureList';
import { MinYearlyTotalPrice } from '../yearlyTotal/MinYearlyTotalPrice';
import { PlanTotal } from '../planTotal/PlanTotal';
import { getStyleProps } from './ProductFooter.style';
import { AddOnsView } from '../addOns/AddOnsView';

interface ProductFooterProps {
  theme: string;
  productDisplayName: string;
}

const getStyle = (theme: string) => (
  theme === FEELIX_THEMES.COMMON ? commonStyle : defaultStyle
);

const getPricing = (productData: ProductData, promoCode: string | undefined) =>
  ({
    price: getTagPrice(productData?.items, promoCode, productData?.region),
    currency: productData.currency || 'AUD',
    isExcludeTax: isNZProduct(productData?.items[0]?.productId)
    // when annual is moved to nbs this will be dynamic
  });

export const ProductFooter: React.FC<ProductFooterProps> =
  ({
    theme,
    productDisplayName
  }) => {
    const {
      productData,
      baseFeatureList,
      baseAndModuleFeatureList,
      productId,
      showModulePrice,
      promoCode,
      availableTo
    } = useProductData();
    const { addonIds } = useSessionContext();
    const style = getStyle(theme);
    const styleProps = getStyleProps(theme);

    const isCommonTheme = theme === FEELIX_THEMES.COMMON;
    const addOnProductsData = productData.items[0]?.subProducts?.filter(
      (product) => addonIds?.includes(product.items[0]?.productId)
    ) ?? [];

    return (
      <Box
        className={style.productFooter}
        {...styleProps.footer}
      >
        <AddOnsView productData={addOnProductsData} />
        {isCommonTheme ? (
          <Box className={style.pricingFeatureSection}>
            <PricingCard
              pricing={getPricing(productData, promoCode)}
              theme={theme}
              productDisplayName={productDisplayName}
            />
            <FeatureList
              items={showModulePrice(productId) ? baseAndModuleFeatureList : baseFeatureList}
              theme={theme}
            />
          </Box>
        ) : (
          <>
            <PricingCard
              pricing={getPricing(productData, promoCode)}
              theme={theme}
            />
            <FeatureList
              items={showModulePrice(productId) ? baseAndModuleFeatureList : baseFeatureList}
              theme={theme}
            />
          </>
        )}
        {isCommonTheme && (
          <>
            <PlanTotal productData={productData} addOnProductsData={addOnProductsData} />
            <MinYearlyTotalPrice
              productData={productData}
              addOnProductsData={addOnProductsData}
            />
          </>
        )}
        { promoCode && availableTo && <TermsConditions className={style.termsAndConditions}/>}
      </Box>
    );
  };
